import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './MyPage.css';

function MyPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [scraps, setScraps] = useState([]);
  const [posts, setPosts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    switch (activeTab) {
      case 'profile':
        fetchProfile();
        break;
      case 'scraps':
        fetchScraps();
        break;
      case 'posts':
        fetchPosts();
        break;
      case 'friends':
        fetchFriends();
        break;
      case 'messages':
        fetchMessages();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchWithAuth = async (url) => {
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 401 || response.status === 403) {
      alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      throw new Error('Unauthorized');
    }

    return response.json();
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/mypage/profile`);
      setProfile(data);
    } catch (error) {
      console.error('Profile fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScraps = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/mypage/scraps`);
      setScraps(data);
    } catch (error) {
      console.error('Scraps fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/mypage/posts`);
      setPosts(data);
    } catch (error) {
      console.error('Posts fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFriends = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/mypage/friends`);
      setFriends(data);
    } catch (error) {
      console.error('Friends fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/mypage/messages`);
      setMessages(data);
    } catch (error) {
      console.error('Messages fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const hour = dateStr.substring(8, 10);
    const minute = dateStr.substring(10, 12);
    return `${year}.${month}.${day} ${hour}:${minute}`;
  };

  return (
    <div className="mypage">
      <div className="mypage-header">
        <h1>마이페이지</h1>
      </div>

      {/* Sub Navigation */}
      <div className="mypage-navigation">
        <button
          className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          회원정보
        </button>
        <button
          className={`nav-tab ${activeTab === 'scraps' ? 'active' : ''}`}
          onClick={() => setActiveTab('scraps')}
        >
          스크랩
        </button>
        <button
          className={`nav-tab ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          작성글
        </button>
        <button
          className={`nav-tab ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          친구
        </button>
        <button
          className={`nav-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          쪽지함
        </button>
      </div>

      {/* Content */}
      <div className="mypage-content">
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <>
            {activeTab === 'profile' && profile && (
              <div className="profile-section">
                <h2>회원정보</h2>
                <table className="info-table">
                  <tbody>
                    <tr>
                      <th>아이디</th>
                      <td>{profile.user_id}</td>
                    </tr>
                    <tr>
                      <th>닉네임</th>
                      <td>{profile.nick_name}</td>
                    </tr>
                    <tr>
                      <th>이메일</th>
                      <td>{profile.email}</td>
                    </tr>
                    <tr>
                      <th>가입일</th>
                      <td>{formatDate(profile.regdate)}</td>
                    </tr>
                    <tr>
                      <th>최근 로그인</th>
                      <td>{formatDate(profile.last_login)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'scraps' && (
              <div className="list-section">
                <h2>스크랩 목록 ({scraps.length})</h2>
                {scraps.length > 0 ? (
                  <table className="post-table">
                    <thead>
                      <tr>
                        <th className="col-num">번호</th>
                        <th className="col-title">제목</th>
                        <th className="col-author">작성자</th>
                        <th className="col-date">날짜</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scraps.map((scrap, idx) => (
                        <tr key={scrap.document_srl}>
                          <td className="col-num">{scraps.length - idx}</td>
                          <td className="col-title">
                            <Link to={`/board/${scrap.board_id}/${scrap.document_srl}`}>
                              {scrap.title}
                            </Link>
                          </td>
                          <td className="col-author">{scrap.author}</td>
                          <td className="col-date">{formatDate(scrap.regdate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">스크랩한 글이 없습니다.</div>
                )}
              </div>
            )}

            {activeTab === 'posts' && (
              <div className="list-section">
                <h2>작성글 목록 ({posts.length})</h2>
                {posts.length > 0 ? (
                  <table className="post-table">
                    <thead>
                      <tr>
                        <th className="col-num">번호</th>
                        <th className="col-title">제목</th>
                        <th className="col-author">작성자</th>
                        <th className="col-date">날짜</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((post, idx) => (
                        <tr key={post.document_srl}>
                          <td className="col-num">{posts.length - idx}</td>
                          <td className="col-title">
                            <Link to={`/board/${post.board_id}/${post.document_srl}`}>
                              {post.title}
                            </Link>
                          </td>
                          <td className="col-author">{post.author}</td>
                          <td className="col-date">{formatDate(post.regdate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">작성한 글이 없습니다.</div>
                )}
              </div>
            )}

            {activeTab === 'friends' && (
              <div className="list-section">
                <h2>친구 목록 ({friends.length})</h2>
                {friends.length > 0 ? (
                  <table className="friends-table">
                    <thead>
                      <tr>
                        <th>번호</th>
                        <th>아이디</th>
                        <th>닉네임</th>
                      </tr>
                    </thead>
                    <tbody>
                      {friends.map((friend, idx) => (
                        <tr key={friend.friend_srl}>
                          <td>{friends.length - idx}</td>
                          <td>{friend.user_id}</td>
                          <td>{friend.nick_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">친구가 없습니다.</div>
                )}
              </div>
            )}

            {activeTab === 'messages' && (
              <div className="list-section">
                <h2>쪽지함 ({messages.length})</h2>
                {messages.length > 0 ? (
                  <table className="messages-table">
                    <thead>
                      <tr>
                        <th className="col-num">번호</th>
                        <th className="col-sender">보낸이</th>
                        <th className="col-title">제목</th>
                        <th className="col-status">상태</th>
                        <th className="col-date">날짜</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((msg, idx) => (
                        <tr key={msg.message_srl} className={msg.readed === 'N' ? 'unread' : ''}>
                          <td className="col-num">{messages.length - idx}</td>
                          <td className="col-sender">{msg.sender_name}</td>
                          <td className="col-title">{msg.title}</td>
                          <td className="col-status">{msg.readed === 'Y' ? '읽음' : '안읽음'}</td>
                          <td className="col-date">{formatDate(msg.regdate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">쪽지가 없습니다.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MyPage;
